import React, { useEffect } from 'react';

const ASI1Mini = () => {
  // Ensure scroll position is at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="container mx-auto sm:px-6 md:px-4 sm:py-8 max-w-7xl" style={{ paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-12 text-white">
          <h1 className="text-4xl font-bold mb-4">ASI 1-Mini</h1>
          <p className="text-xl opacity-90">
            Efficient Reasoning for Everyday Agent Workflows
          </p>
        </div>

        {/* Documentation content */}
        <div className="px-6 md:px-12 lg:px-16 py-8">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              ASI 1-Mini is our streamlined, closed-source reasoning model built for common agent discovery and day-to-day tasks. While its larger sibling (ASI 1-Mini-Extended) tackles deep multistep analysis, ASI 1-Mini focuses on speed, simplicity, and resource-frugal deployment—the perfect engine for chatbots, scheduling agents, customer-support flows, and light on-chain utilities.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Performance Highlights</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Metric</th>
                      <th className="text-left p-4 border-b font-semibold">ASI 1-Mini</th>
                      <th className="text-left p-4 border-b font-semibold">ASI 1-Mini-Extended*</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b">MMLU Benchmark</td>
                      <td className="p-4 border-b font-bold text-blue-700">85 %</td>
                      <td className="p-4 border-b">89 %</td>
                    </tr>
                    <tr>
                      <td className="p-4">Context Window</td>
                      <td className="p-4 font-bold text-blue-700">32 k tokens</td>
                      <td className="p-4">64 k tokens</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4">*Shown for reference; Mini-Extended is a separate, higher-capacity model.</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Why Choose ASI 1-Mini?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Fast, Low-Friction Deployment</h3>
                <p className="text-gray-700">
                  Runs comfortably on mid-range GPUs or modest cloud VMs without specialised hardware.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Everyday Multitask Reasoning</h3>
                <p className="text-gray-700">
                  Optimised for short reasoning chains: summarisation, classification, calendar builds, quick contract checks.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Agent Discovery Primitives</h3>
                <p className="text-gray-700">
                  Exposes function names, argument schemas, and tool IDs in a single pass—ideal for orchestrators that must select the right sub-agent fast.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Lightweight Self-Correction</h3>
                <p className="text-gray-700">
                  Sub-2 ms sanity loops catch arithmetic slips or JSON-schema mismatches before emitting answers.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Safety Filters Built-In</h3>
                <p className="text-gray-700">
                  Compact rule-based guardrails screen known jailbreak strings and unsafe transaction patterns.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Typical Use-Cases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Customer-Support Bots</h3>
                <p className="text-gray-700">
                  Resolve tickets, provide FAQ answers, escalate only when necessary.
                </p>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Personal & Team Scheduling Agents</h3>
                <p className="text-gray-700">
                  Parse natural-language intents, coordinate calendars, draft reminders, and handle rescheduling conflicts.
                </p>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">E-commerce Concierge</h3>
                <p className="text-gray-700">
                  Classify products, recommend bundles, and generate short persuasive descriptions.
                </p>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">On-Chain Utility Helpers</h3>
                <p className="text-gray-700">
                  Run light contract sanity checks, surface obvious security smells, and suggest gas-saving refactors.
                </p>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Enterprise Data Routers</h3>
                <p className="text-gray-700">
                  Triage incoming emails or Slack messages, label them, and hand them off to specialised downstream agents.
                </p>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">IoT Voice Assistants</h3>
                <p className="text-gray-700">
                  Translate user commands into structured API calls for smart-home ecosystems with ultra-low latency.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Trust & Transparency</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Capability</th>
                      <th className="text-left p-4 border-b font-semibold">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b font-medium">Traceable Thoughts</td>
                      <td className="p-4 border-b">Returns concise reasoning snippets (≤ 4 steps) with SHA-256 hash for provenance logging.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Controlled Hallucination Rate</td>
                      <td className="p-4 border-b">≤ 1.2 % unsupported claims in everyday domains after post-filtering.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Security Awareness</td>
                      <td className="p-4">Flags re-entrancy or allowance-change risks when scanning ERC-20/721 snippets, recommending common-sense mitigations.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Roadmap & Upcoming Features</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">ETA</th>
                      <th className="text-left p-4 border-b font-semibold">Feature</th>
                      <th className="text-left p-4 border-b font-semibold">Benefit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b font-medium">Q3 2025</td>
                      <td className="p-4 border-b">Dynamic Tool-Calling API</td>
                      <td className="p-4 border-b">Automatic function invocation with per-tool confidence scores.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Q4 2025</td>
                      <td className="p-4 border-b">Edge Optimisation Pack</td>
                      <td className="p-4 border-b">3-bit compression + Flash-Attention v3 kernels for phone-class NPUs.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Early 2026</td>
                      <td className="p-4 border-b">Conversational RLHF Add-On</td>
                      <td className="p-4 border-b">Brand-voice adaptation and regional dialect fine-tuning.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">2026</td>
                      <td className="p-4">Federated Fine-Tuning</td>
                      <td className="p-4">On-device learning so assistants adapt without exporting raw data.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6">
              Utilize ASI 1-Mini today for a nimble, trustworthy brain in your everyday agents—and step up to ASI 1-Mini-Extended whenever you need deeper, audit-grade reasoning.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ASI1Mini;
